import { AppError } from '../domain/errors'
import type { PasswordHash, PasswordHasherPort } from '../domain/ports'

const SALT_BYTES = 16
const DEFAULT_ITERATIONS = 120_000

const toHex = (bytes: Uint8Array): string =>
  [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('')

const fromHex = (value: string): Uint8Array => {
  if (!/^[a-f\d]+$/i.test(value) || value.length % 2 !== 0) {
    throw new AppError('auth_unavailable', 'Stored sign-in data is not readable.', 'error')
  }

  const output = new Uint8Array(value.length / 2)
  for (let index = 0; index < output.length; index += 1) {
    output[index] = Number.parseInt(value.slice(index * 2, index * 2 + 2), 16)
  }
  return output
}

const bytesToBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const output = new Uint8Array(bytes.length)
  output.set(bytes)
  return output.buffer
}

const getWebCrypto = (): Crypto => {
  const cryptoApi = globalThis.crypto
  if (!cryptoApi?.subtle || typeof cryptoApi.getRandomValues !== 'function') {
    throw new AppError('auth_unavailable', 'Secure sign-in is not available in this browser.', 'unavailable')
  }
  return cryptoApi
}

const constantTimeEqual = (left: string, right: string): boolean => {
  const max = Math.max(left.length, right.length)
  let mismatch = left.length ^ right.length
  for (let index = 0; index < max; index += 1) {
    mismatch |= (left.charCodeAt(index) || 0) ^ (right.charCodeAt(index) || 0)
  }
  return mismatch === 0
}

export class WebCryptoPasswordHasher implements PasswordHasherPort {
  private readonly iterations: number
  private readonly saltFactory?: () => Uint8Array

  constructor(options: { iterations?: number; saltFactory?: () => Uint8Array } = {}) {
    this.iterations = options.iterations ?? DEFAULT_ITERATIONS
    this.saltFactory = options.saltFactory
  }

  async hash(password: string, existingSalt?: string): Promise<PasswordHash> {
    const cryptoApi = getWebCrypto()
    const saltBytes = existingSalt
      ? fromHex(existingSalt)
      : this.saltFactory?.() ?? cryptoApi.getRandomValues(new Uint8Array(SALT_BYTES))
    const material = await cryptoApi.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      'PBKDF2',
      false,
      ['deriveBits'],
    )
    const bits = await cryptoApi.subtle.deriveBits(
      {
        name: 'PBKDF2',
        hash: 'SHA-256',
        salt: bytesToBuffer(saltBytes),
        iterations: this.iterations,
      },
      material,
      256,
    )
    return { salt: toHex(saltBytes), digest: toHex(new Uint8Array(bits)) }
  }

  async verify(password: string, value: PasswordHash): Promise<boolean> {
    const candidate = await this.hash(password, value.salt)
    return constantTimeEqual(candidate.digest, value.digest)
  }
}

/**
 * Test-only deterministic PBKDF2 configuration. It retains the same Web Crypto
 * algorithm while replacing random salt generation so fixtures are repeatable.
 */
export const createDeterministicTestPasswordHasher = (): PasswordHasherPort =>
  new WebCryptoPasswordHasher({
    iterations: 1_000,
    saltFactory: () => Uint8Array.from([
      0x54, 0x68, 0x65, 0x50, 0x61, 0x72, 0x61, 0x64,
      0x6f, 0x78, 0x54, 0x65, 0x73, 0x74, 0x21, 0x21,
    ]),
  })
